import { supabase } from '../lib/supabase';

export const auditService = {
  /**
   * Fetch audit logs with filters
   */
  async getAuditLogs(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          performed_by_user:user_profiles!audit_logs_performed_by_fkey(email, first_name, last_name),
          affected_user:user_profiles!audit_logs_affected_user_id_fkey(email, first_name, last_name),
          affected_role:system_roles(name, display_name)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.actionType && filters?.actionType?.length > 0) {
        query = query?.in('action_type', filters?.actionType);
      }
      if (filters?.performedBy) {
        query = query?.eq('performed_by', filters?.performedBy);
      }
      if (filters?.affectedUser) {
        query = query?.eq('affected_user_id', filters?.affectedUser);
      }
      if (filters?.searchText) {
        query = query?.ilike('action_description', `%${filters?.searchText}%`);
      }

      // Pagination
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }
      if (filters?.offset) {
        query = query?.range(filters?.offset, filters?.offset + (filters?.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase
      return data?.map(log => ({
        id: log?.id,
        tenantId: log?.tenant_id,
        actionType: log?.action_type,
        actionDescription: log?.action_description,
        performedBy: log?.performed_by,
        performedByUser: log?.performed_by_user,
        affectedUserId: log?.affected_user_id,
        affectedUser: log?.affected_user,
        affectedRoleId: log?.affected_role_id,
        affectedRole: log?.affected_role,
        beforeState: log?.before_state,
        afterState: log?.after_state,
        changeDetails: log?.change_details,
        ipAddress: log?.ip_address,
        userAgent: log?.user_agent,
        createdAt: log?.created_at
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  /**
   * Get audit statistics
   */
  async getAuditStatistics(startDate, endDate) {
    try {
      let query = supabase?.from('audit_statistics')?.select('*');

      if (startDate) {
        query = query?.gte('activity_date', startDate);
      }
      if (endDate) {
        query = query?.lte('activity_date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(stat => ({
        tenantId: stat?.tenant_id,
        actionType: stat?.action_type,
        actionCount: stat?.action_count,
        activityDate: stat?.activity_date
      }));
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      throw error;
    }
  },

  /**
   * Get unique users who performed actions
   */
  async getAuditActors() {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('performed_by, performed_by_user:user_profiles!audit_logs_performed_by_fkey(email, first_name, last_name)')?.not('performed_by', 'is', null);

      if (error) throw error;

      // Remove duplicates
      const uniqueActors = new Map();
      data?.forEach(log => {
        if (log?.performed_by && log?.performed_by_user) {
          uniqueActors?.set(log?.performed_by, {
            id: log?.performed_by,
            email: log?.performed_by_user?.email,
            firstName: log?.performed_by_user?.first_name,
            lastName: log?.performed_by_user?.last_name
          });
        }
      });

      return Array.from(uniqueActors?.values());
    } catch (error) {
      console.error('Error fetching audit actors:', error);
      throw error;
    }
  },

  /**
   * Export audit logs to CSV
   */
  async exportAuditLogs(filters = {}) {
    try {
      const logs = await this.getAuditLogs(filters);
      
      // Create CSV content
      const headers = [
        'Timestamp',
        'Action Type',
        'Description',
        'Performed By',
        'Affected User',
        'IP Address',
        'Before State',
        'After State'
      ];

      const rows = logs?.map(log => [
        new Date(log.createdAt)?.toLocaleString(),
        log?.actionType,
        log?.actionDescription,
        log?.performedByUser ? `${log?.performedByUser?.first_name} ${log?.performedByUser?.last_name} (${log?.performedByUser?.email})` : 'System',
        log?.affectedUser ? `${log?.affectedUser?.first_name} ${log?.affectedUser?.last_name} (${log?.affectedUser?.email})` : 'N/A',
        log?.ipAddress || 'N/A',
        log?.beforeState ? JSON.stringify(log?.beforeState) : 'N/A',
        log?.afterState ? JSON.stringify(log?.afterState) : 'N/A'
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      return csvContent;
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  },

  /**
   * Manual audit log creation (for actions not automatically tracked)
   */
  async createAuditLog(logData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('audit_logs')?.insert({
          tenant_id: logData?.tenantId,
          action_type: logData?.actionType,
          action_description: logData?.actionDescription,
          performed_by: user?.id,
          affected_user_id: logData?.affectedUserId,
          affected_role_id: logData?.affectedRoleId,
          before_state: logData?.beforeState,
          after_state: logData?.afterState,
          change_details: logData?.changeDetails,
          ip_address: logData?.ipAddress,
          user_agent: logData?.userAgent
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        actionType: data?.action_type,
        actionDescription: data?.action_description,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating audit log:', error);
      throw error;
    }
  }
};